/* KL Shipping Estimator (OC2 v3) - one field, checkout-address-validation
   style: type a 4-digit postcode, pick a "Suburb STATE 4060" suggestion,
   get the shipping cost for the quantity on the page. Members price from
   the flat rate zones (instant); everyone else gets a live quote from
   the same rating source checkout uses (server-cached). Destination is
   remembered on the device and re-estimates automatically. Fails soft:
   any error shows the fallback line and never blocks the page. */
(function () {
  "use strict";

  var TIMEOUT_MS = 12000; // live quotes can take a couple of seconds
  var STORE_KEY = "klsc:dest";
  var UID = 0;

  function fetchJson(url) {
    return new Promise(function (resolve, reject) {
      var controller =
        typeof AbortController !== "undefined" ? new AbortController() : null;
      var timer = setTimeout(function () {
        if (controller) controller.abort();
        reject(new Error("timeout"));
      }, TIMEOUT_MS);
      fetch(url, controller ? { signal: controller.signal } : {})
        .then(function (r) {
          clearTimeout(timer);
          if (!r.ok) return reject(new Error("http " + r.status));
          return r.json().then(resolve, reject);
        })
        .catch(function (e) {
          clearTimeout(timer);
          reject(e);
        });
    });
  }

  function fill(template, values) {
    return template.replace(
      /\[(total|price|parcels|suburb|state|postcode|zone|dates)\]/g,
      function (_, k) {
        return values[k] != null ? String(values[k]) : "";
      }
    );
  }

  function money(cents) {
    return "$" + (cents / 100).toFixed(2);
  }

  function readStored() {
    try {
      var raw = window.localStorage.getItem(STORE_KEY);
      if (!raw) return null;
      var d = JSON.parse(raw);
      return d && d.pc && d.key ? d : null;
    } catch (e) {
      return null;
    }
  }

  function writeStored(dest) {
    try {
      if (dest) window.localStorage.setItem(STORE_KEY, JSON.stringify(dest));
      else window.localStorage.removeItem(STORE_KEY);
    } catch (e) {
      /* private mode etc - remembering is optional */
    }
  }

  /* Zones popup (2026-08-22 consolidation, option B): the table is fed
     LIVE from ShipHound's /checkout/widget/zones - same world as the
     answers, so it flips at run approval with everything else. Fetch
     fires on INTENT (pointer/touch/focus on the trigger) so the data is
     usually there before the tap completes; cached on the device for
     ZONES_TTL_MS; on any failure the popup keeps just the S&D link. */
  var ZONES_KEY = "klsc:zones";
  var ZONES_TTL_MS = 600000; // 10 min - a popup lags an approval by at most this

  function initZones(root, endpoint) {
    var box = root.querySelector("[data-kl-sc-zones]");
    if (!box || box.dataset.klScZonesReady) return;
    box.dataset.klScZonesReady = "1";
    var details = box.closest("details");
    var summary = details ? details.querySelector("summary") : null;
    var fetched = false;

    function render(zones) {
      if (!zones || !zones.length) return; // fail-soft: the S&D link stands
      // Mirrors the Shipping & Delivery page's table (Boris, 22-Aug):
      // no header row; zone + price share the left cell, coverage right.
      var priceLine = box.dataset.priceLine || "[price] per parcel";
      var table = document.createElement("table");
      var tbody = document.createElement("tbody");
      zones.forEach(function (z) {
        var tr = document.createElement("tr");
        var left = document.createElement("td");
        var strong = document.createElement("strong");
        strong.textContent = z.zone;
        left.appendChild(strong);
        left.appendChild(document.createElement("br"));
        left.appendChild(
          document.createTextNode(priceLine.replace("[price]", "$" + z.price_inc))
        );
        var right = document.createElement("td");
        right.textContent = z.where || "";
        tr.appendChild(left);
        tr.appendChild(right);
        tbody.appendChild(tr);
      });
      if (box.dataset.rest) {
        var restTr = document.createElement("tr");
        var restTd = document.createElement("td");
        restTd.colSpan = 2;
        restTd.textContent = box.dataset.rest;
        restTr.appendChild(restTd);
        tbody.appendChild(restTr);
      }
      table.appendChild(tbody);
      box.textContent = "";
      box.appendChild(table);
    }

    function load() {
      if (fetched) return;
      fetched = true;
      try {
        var raw = window.localStorage.getItem(ZONES_KEY);
        if (raw) {
          var c = JSON.parse(raw);
          if (c && c.t && Date.now() - c.t < ZONES_TTL_MS && c.zones && c.zones.length) {
            render(c.zones);
            return;
          }
        }
      } catch (e) {
        /* cache optional */
      }
      fetchJson(endpoint + "/checkout/widget/zones")
        .then(function (data) {
          var zones = data && data.zones;
          if (!zones || !zones.length) return;
          try {
            window.localStorage.setItem(
              ZONES_KEY,
              JSON.stringify({ t: Date.now(), zones: zones })
            );
          } catch (e) {
            /* private mode - fine */
          }
          render(zones);
        })
        .catch(function () {
          fetched = false; // retry on the next intent signal
        });
    }

    if (summary) {
      ["pointerenter", "touchstart", "focus", "click"].forEach(function (ev) {
        summary.addEventListener(ev, load, { passive: true });
      });
    }
    if (details) details.addEventListener("toggle", load);
  }

  function init(root) {
    if (root.dataset.klScReady) return;
    root.dataset.klScReady = "1";

    var endpoint = (root.dataset.endpoint || "").replace(/\/+$/, "");
    if (endpoint) initZones(root, endpoint);
    var copy = {
      memberOne: root.dataset.copyMemberOne || "",
      memberMany: root.dataset.copyMemberMany || "",
      zoneLine: root.dataset.copyZoneLine || "",
      quoteOne: root.dataset.copyQuoteOne || "",
      quoteMany: root.dataset.copyQuoteMany || "",
      quoteLoading: root.dataset.copyQuoteLoading || "",
      nonmember: root.dataset.copyNonmember || "",
      zoneQuote: root.dataset.copyZoneQuote || "",
      etaLine: root.dataset.copyEtaLine || "",
      nopostcode: root.dataset.copyNopostcode || "",
      fallback: root.dataset.copyFallback || ""
    };
    var factor = parseFloat(root.dataset.factor) || 1;
    var unitKg = parseFloat(root.dataset.unitKg) || 0; // the product's chargeable weight
    var mode = root.dataset.mode || "estimate"; // "zone" = the S&D lookup
    var input = root.querySelector("[data-kl-sc-input]");
    var listEl = root.querySelector("[data-kl-sc-list]");
    var statusEl = root.querySelector("[data-kl-sc-status]");
    var answerEl = root.querySelector("[data-kl-sc-answer]");
    if (!endpoint || !input || !listEl || !answerEl) return;

    var listId = "kl-sc-list-" + ++UID;
    listEl.id = listId;
    input.setAttribute("aria-controls", listId);

    var seq = 0;
    var items = [];
    var itemsPc = "";
    var itemsState = "";
    var highlight = -1;
    var selected = null; // {pc, key, display, state, fieldValue}
    var lastLookup = null; // the member/non-member answer for `selected`
    var quoteCache = {}; // parcels -> quote payload (this destination)

    // The PDP quantity selector, when one exists on the page. The dummy
    // volume products convert via data-factor (parcels per unit).
    var qtyEl =
      document.querySelector('form[action*="/cart/add"] input[name="quantity"]') ||
      document.querySelector('input[name="quantity"]');

    function parcelsNow() {
      if (mode === "zone") return 1;
      var qty = 1;
      if (qtyEl) {
        var v = parseInt(qtyEl.value, 10);
        if (v > 0) qty = v;
      }
      return Math.max(1, Math.ceil(qty * factor));
    }

    function setStatus(text) {
      if (!statusEl) return;
      statusEl.hidden = !text;
      statusEl.textContent = text || "";
    }

    function clearAnswer() {
      answerEl.textContent = "";
    }

    function lineEl(text, cls, emphasise) {
      var p = document.createElement("p");
      p.className = cls;
      if (emphasise) {
        var m = text.match(/\$[0-9,]+\.[0-9]{2}/);
        if (m) {
          var i = text.indexOf(m[0]);
          p.appendChild(document.createTextNode(text.slice(0, i)));
          var strong = document.createElement("strong");
          strong.textContent = m[0];
          p.appendChild(strong);
          p.appendChild(document.createTextNode(text.slice(i + m[0].length)));
          return p;
        }
      }
      p.textContent = text;
      return p;
    }

    function showLines(lines) {
      clearAnswer();
      var box = document.createElement("div");
      box.className = "kl-sc__answer-box";
      lines.forEach(function (l) {
        if (l && l.text) box.appendChild(lineEl(l.text, l.cls || "", !!l.money));
      });
      if (box.children.length) answerEl.appendChild(box);
    }

    function showFallback() {
      setStatus("");
      if (copy.fallback)
        showLines([{ text: copy.fallback, cls: "kl-sc__line" }]);
    }

    function etaLine(values) {
      if (!copy.etaLine || !values.dates) return null;
      return { text: fill(copy.etaLine, values), cls: "kl-sc__line kl-sc__line--eta" };
    }

    function renderMember(look, parcels) {
      var priceCents = Math.round(parseFloat(look.price_inc) * 100);
      var values = {
        total: money(priceCents * parcels),
        price: money(priceCents),
        parcels: parcels,
        suburb: look.suburb_display,
        state: look.state || "",
        postcode: look.postcode,
        zone: look.zone,
        dates: look.delivery_estimate || ""
      };
      var template = parcels === 1 ? copy.memberOne : copy.memberMany;
      var zoneLine = mode === "zone" ? null : copy.zoneLine; // zone mode's member template already names the zone
      showLines([
        zoneLine ? { text: fill(zoneLine, values), cls: "kl-sc__line" } : null,
        { text: fill(template, values), cls: "kl-sc__line kl-sc__line--money", money: true },
        etaLine(values)
      ]);
    }

    function renderQuote(q, parcels) {
      var values = {
        total: "$" + q.quote_inc,
        price: "",
        parcels: parcels,
        suburb: q.suburb_display,
        state: q.state || "",
        postcode: q.postcode,
        zone: "",
        dates: q.delivery_estimate || ""
      };
      var template = parcels === 1 ? copy.quoteOne : copy.quoteMany;
      showLines([
        { text: fill(template, values), cls: "kl-sc__line kl-sc__line--money", money: true },
        etaLine(values)
      ]);
    }

    function estimate() {
      if (!selected) return;
      var parcels = parcelsNow();
      var mySeq = ++seq;
      if (lastLookup && lastLookup.member === true) {
        renderMember(lastLookup, parcels);
        return;
      }
      if (lastLookup && quoteCache[parcels]) {
        renderQuote(quoteCache[parcels], parcels);
        return;
      }
      setStatus(copy.quoteLoading || "");
      if (!lastLookup) clearAnswer();
      var dest = selected;
      fetchJson(
        endpoint +
          "/checkout/widget/quote?postcode=" + dest.pc +
          "&suburb=" + encodeURIComponent(dest.key) +
          "&parcels=" + parcels +
          (unitKg > 0 ? "&unit_kg=" + unitKg : "")
      )
        .then(function (q) {
          if (mySeq !== seq || selected !== dest) return;
          setStatus("");
          if (q && q.member === true && q.price_inc) {
            lastLookup = {
              member: true, price_inc: q.price_inc, zone: q.zone,
              suburb_display: q.suburb_display, postcode: q.postcode,
              state: q.state, delivery_estimate: q.delivery_estimate
            };
            renderMember(lastLookup, parcels);
            return;
          }
          if (q && q.quote_inc) {
            quoteCache[parcels] = q;
            renderQuote(q, parcels);
            return;
          }
          showFallback();
        })
        .catch(function () {
          if (mySeq !== seq || selected !== dest) return;
          showFallback();
        });
    }

    function beginEstimate() {
      if (!selected) return;
      var dest = selected;
      var mySeq = ++seq;
      setStatus("Checking…");
      clearAnswer();
      fetchJson(
        endpoint +
          "/checkout/widget/lookup?postcode=" + dest.pc +
          "&suburb=" + encodeURIComponent(dest.key)
      )
        .then(function (look) {
          if (mySeq !== seq || selected !== dest) return;
          setStatus("");
          if (look && look.member === true && look.price_inc) {
            lastLookup = look;
            renderMember(look, parcelsNow());
          } else if (look && look.postcode && mode === "zone") {
            // the S&D zone lookup: the honest no-zone line up front, then
            // the per-parcel live quote joins it (no quantity assumed)
            var values = {
              suburb: look.suburb_display, state: look.state || "",
              postcode: look.postcode, dates: look.delivery_estimate || ""
            };
            var zoneLines = function (quoteText) {
              return [
                copy.nonmember ? { text: fill(copy.nonmember, values), cls: "kl-sc__line" } : null,
                quoteText ? { text: quoteText, cls: "kl-sc__line kl-sc__line--money", money: true } : null,
                etaLine(values)
              ];
            };
            showLines(zoneLines(null));
            if (copy.zoneQuote) {
              var zSeq = seq;
              setStatus(copy.quoteLoading || "");
              fetchJson(
                endpoint + "/checkout/widget/quote?postcode=" + dest.pc +
                  "&suburb=" + encodeURIComponent(dest.key) + "&parcels=1"
              )
                .then(function (q) {
                  if (zSeq !== seq || selected !== dest) return;
                  setStatus("");
                  if (q && q.quote_inc) {
                    values.total = "$" + q.quote_inc;
                    values.dates = q.delivery_estimate || values.dates;
                    showLines(zoneLines(fill(copy.zoneQuote, values)));
                  }
                })
                .catch(function () {
                  if (zSeq !== seq || selected !== dest) return;
                  setStatus(""); // the no-zone line already stands - quote is a bonus
                });
            }
          } else if (look && look.postcode) {
            lastLookup = look; // non-member - quotes come per parcels count
            estimate();
          } else {
            showFallback();
          }
        })
        .catch(function () {
          if (mySeq !== seq || selected !== dest) return;
          showFallback();
        });
    }

    function closeList() {
      listEl.hidden = true;
      listEl.textContent = "";
      input.setAttribute("aria-expanded", "false");
      input.removeAttribute("aria-activedescendant");
      items = [];
      highlight = -1;
    }

    function setHighlight(idx) {
      var lis = listEl.children;
      if (highlight >= 0 && lis[highlight])
        lis[highlight].setAttribute("aria-selected", "false");
      highlight = idx;
      if (idx >= 0 && lis[idx]) {
        lis[idx].setAttribute("aria-selected", "true");
        input.setAttribute("aria-activedescendant", lis[idx].id);
        if (lis[idx].scrollIntoView) lis[idx].scrollIntoView({ block: "nearest" });
      } else {
        input.removeAttribute("aria-activedescendant");
      }
    }

    function displayFor(s, pc, state) {
      return (s.display || s.key) + (state ? " " + state : "") + " " + pc;
    }

    function choose(idx) {
      var s = items[idx];
      if (!s) return;
      var fieldValue = displayFor(s, itemsPc, itemsState);
      selected = {
        pc: itemsPc, key: s.key, display: s.display || s.key,
        state: itemsState, fieldValue: fieldValue
      };
      lastLookup = null;
      quoteCache = {};
      input.value = fieldValue;
      writeStored(selected);
      closeList();
      beginEstimate();
    }

    function openList(subs, pc, state) {
      listEl.textContent = "";
      items = subs;
      itemsPc = pc;
      itemsState = state || "";
      subs.forEach(function (s, idx) {
        if (!s || !s.key) return;
        var li = document.createElement("li");
        li.id = listId + "-opt-" + idx;
        li.setAttribute("role", "option");
        li.setAttribute("aria-selected", "false");
        li.textContent = displayFor(s, pc, itemsState);
        li.addEventListener("mousedown", function (e) {
          e.preventDefault(); // keep focus on the input - no blur
        });
        li.addEventListener("click", function () {
          choose(idx);
        });
        listEl.appendChild(li);
      });
      listEl.hidden = false;
      input.setAttribute("aria-expanded", "true");
      highlight = -1;
    }

    function loadSuggestions(pc) {
      var mySeq = ++seq;
      setStatus("Checking…");
      clearAnswer();
      fetchJson(endpoint + "/checkout/widget/suburbs?postcode=" + pc)
        .then(function (data) {
          if (mySeq !== seq) return;
          setStatus("");
          var subs = (data && data.suburbs) || [];
          if (!subs.length) {
            closeList();
            if (copy.nopostcode)
              showLines([{ text: fill(copy.nopostcode, { postcode: pc }), cls: "kl-sc__line" }]);
            return;
          }
          openList(subs, pc, data.state || "");
        })
        .catch(function () {
          if (mySeq !== seq) return;
          closeList();
          showFallback();
        });
    }

    input.addEventListener("input", function () {
      if (selected !== null) {
        if (input.value === selected.fieldValue) return;
        selected = null; // editing a picked destination starts over
        lastLookup = null;
        quoteCache = {};
        writeStored(null);
        input.value = "";
      }
      var digits = (input.value || "").replace(/\D/g, "").slice(0, 4);
      if (input.value !== digits) input.value = digits;
      seq++;
      closeList();
      clearAnswer();
      setStatus("");
      if (digits.length === 4) loadSuggestions(digits);
    });

    input.addEventListener("keydown", function (e) {
      // Enter/Done is handled even with the list closed (Boris's 22-Aug
      // mobile finding): the keypad's action key must never submit the
      // surrounding product form or hop focus down the page - it closes
      // the keypad (blur) and the suggestions stay put so the newly
      // revealed viewport shows them.
      if (e.key === "Enter") {
        e.preventDefault();
        if (!listEl.hidden && highlight >= 0) {
          choose(highlight);
        } else {
          input.blur();
        }
        return;
      }
      if (listEl.hidden) return;
      var n = listEl.children.length;
      if (e.key === "ArrowDown" && n) {
        e.preventDefault();
        setHighlight((highlight + 1) % n);
      } else if (e.key === "ArrowUp" && n) {
        e.preventDefault();
        setHighlight((highlight - 1 + n) % n);
      } else if (e.key === "Escape" || e.key === "Tab") {
        closeList();
      }
    });

    // The list closes on a tap/click OUTSIDE the widget, not on blur -
    // so dismissing the mobile keypad (Done, or the OS's own dismiss)
    // reveals the suggestions instead of wiping them. Choosing an entry
    // and Escape/Tab still close it.
    document.addEventListener("pointerdown", function (e) {
      if (!root.contains(e.target)) closeList();
    }, { passive: true });

    if (qtyEl && mode !== "zone") {
      var onQty = function () {
        if (selected) estimate();
      };
      qtyEl.addEventListener("change", onQty);
      qtyEl.addEventListener("input", onQty);
    }

    // A remembered destination estimates straight away - the lowest-
    // friction path for returning shoppers.
    var stored = readStored();
    if (stored && !input.value) {
      selected = stored;
      input.value = stored.fieldValue ||
        stored.display + (stored.state ? " " + stored.state : "") + " " + stored.pc;
      beginEstimate();
    }
  }

  function boot() {
    try {
      var roots = document.querySelectorAll("[data-kl-sc]");
      for (var i = 0; i < roots.length; i++) init(roots[i]);
    } catch (e) {
      /* never block the page */
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
